import { Component, Input } from '@angular/core';
import { describeChar, formatCount, smsParts, SmsParts } from '../../../../../../../utils/sms';

/**
 * Live cost ruler under a message editor: one capsule per SMS part, drawn
 * to scale, then characters, parts and encoding in words. When Unicode
 * raises the cost it names the character responsible; when personalised
 * fields make the length vary it says the count is an estimate.
 * Used by compose and the template editor.
 */
@Component({
  selector: 'compose-sms-meter',
  standalone: true,
  templateUrl: './sms-meter.component.html',
  styleUrl: './sms-meter.component.css',
})
export class SmsMeterComponent {
  parts: SmsParts = smsParts('');
  /** "2 parts, GSM-7": updated only when the part count or encoding changes. */
  announcement = '';
  private seenOnce = false;

  @Input() set text(value: string | null | undefined) {
    const previous = this.parts;
    this.parts = smsParts(value ?? '');
    const changed = previous.parts !== this.parts.parts || previous.encoding !== this.parts.encoding;
    // The first value is what the editor opened with, not something the user did.
    if (this.seenOnce && changed) {
      this.announcement = this.parts.parts ? `${this.partsLabel}, ${this.parts.encoding}` : '';
    }
    this.seenOnce = true;
  }

  /** "142 of 160 characters", "About 312 characters". */
  get lengthLabel(): string {
    const { length, parts, perPart, estimated } = this.parts;
    const about = estimated ? 'About ' : '';
    return parts <= 1
      ? `${about}${formatCount(length)} of ${perPart} characters`
      : `${about}${formatCount(length)} characters`;
  }

  get partsLabel(): string {
    const { parts } = this.parts;
    return parts === 1 ? '1 part' : `${parts} parts`;
  }

  /** Characters left before the next part starts. */
  get roomLeft(): number {
    const { length, parts, perPart } = this.parts;
    if (!parts) return perPart;
    return Math.max(0, parts * perPart - length);
  }

  /** "Unicode, because of an em dash". */
  get encodingNote(): string {
    const { encoding, offender } = this.parts;
    if (encoding !== 'Unicode') return '';
    return offender ? `Unicode, because of ${describeChar(offender)}` : 'Unicode';
  }

  /** Room per part now, and what plain GSM-7 text would allow. */
  get unicodeRoom(): { now: number; plain: number } {
    return this.parts.parts > 1 ? { now: 67, plain: 153 } : { now: 70, plain: 160 };
  }
}
